import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ITenantMeter,
  ISendReadingPayload,
  ISupply,
  IMeterDataDistributed,
  IMeter,
  IReadingPage,
  IMeterData,
  IConsumptionDetail,
  IMeterConsumptionDistributedResponse,
  ISettlementSimulation,
  IResponse,
} from '../core.interface';
import { Spinner } from './spinner';

export interface DailyChartFingerprint {
  tenantId: string;
  supplyTypeId: number;
  meterId: number;
  frequency: string;
  from: string;
  to: string;
  formatType: string;
}

export interface MonthlyChartFingerprint {
  tenantId: string;
  supplyTypeId: number;
  meterId: number;
  frequency: 'MONTH';
  from: string;
  to: string;
}

export interface MonthlyDistributedTableFingerprint {
  tenantId: string;
  supplyTypeId: number;
  from: string;
  to: string;
}

interface DailyMeteringFetchOptions {
  manageSpinner?: boolean;
  fingerprint?: DailyChartFingerprint;
  tableFingerprint?: DailyChartFingerprint;
}

interface MonthlyMeteringFetchOptions {
  fingerprint?: MonthlyChartFingerprint;
}

@Injectable({
  providedIn: 'root',
})
export class Metering {
  private http = inject(HttpClient);

  // Signals para manejar el estado (Equivalente a las variables de Flutter)

  public tenantMeters = signal<ITenantMeter[]>([]);
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);

  private hasFetchedTenantMeters = false;

  /* s */

  public async fetchTenantMeters(tenantId: string, force: boolean = false): Promise<void> {
    if (this.isLoading() && !force) return;
    if (this.tenantMeters().length > 0 && !force && this.hasFetchedTenantMeters) return;

    this.isLoading.set(true);
    this.error.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<{ data?: ITenantMeter[] } | ITenantMeter[]>(
          `${environment.apiUrl}/tenant/${tenantId}/meters`,
        ),
      );

      let rawList: ITenantMeter[] = [];
      if (
        response &&
        typeof response === 'object' &&
        'data' in response &&
        Array.isArray(response.data)
      ) {
        rawList = response.data;
      } else if (Array.isArray(response)) {
        rawList = response;
      }

      this.tenantMeters.set(rawList);
      this.hasFetchedTenantMeters = true;
    } catch (e) {
      this.error.set('Error al cargar contadores');
      this.tenantMeters.set([]);
      console.error(e);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async sendReading(payload: ISendReadingPayload): Promise<boolean> {
    try {
      const formData = new FormData();

      formData.append('tenantId', payload.tenantId);
      formData.append('meterId', payload.meterId);
      formData.append('value', payload.value);

      // 3. Añadimos el archivo si existe (equivalente a MultipartFile)
      if (payload.attachment) {
        formData.append('attachments', payload.attachment, payload.attachment.name);
      }

      const url = `${environment.apiUrl}/tenant/${payload.tenantId}/send-manual-reading`;

      const response = await firstValueFrom(this.http.post(url, formData, { observe: 'response' }));

      return response.status === 200 || response.status === 201;
    } catch (error) {
      console.error('Error sending reading:', error);
      return false;
    }
  }

  private $spinner = inject(Spinner);

  // --- Signals de Estado ---
  public supplies = signal<ISupply[]>([]);
  public meters = signal<IMeter[]>([]);
  // --- Mis consumos (mensual) ---
  public consumptions = signal<IConsumptionDetail[]>([]);
  public consumptionsDistributed = signal<IMeterDataDistributed[]>([]);
  public consumptionsTable = signal<IConsumptionDetail[]>([]);
  public totalElementsSignal = signal(0);
  public totalPagesSignal = signal(0);
  public totalElements = this.totalElementsSignal.asReadonly();
  public totalPages = this.totalPagesSignal.asReadonly();
  public consumptionUnits = signal<string>('');

  // --- Lecturas diarias / horarias (separado del mensual) ---
  public consumptionsDaily = signal<IConsumptionDetail[]>([]);
  public consumptionsTableDaily = signal<IConsumptionDetail[]>([]);
  public totalElementsDailySignal = signal(0);
  public totalPagesDailySignal = signal(0);
  public totalElementsDaily = this.totalElementsDailySignal.asReadonly();
  public totalPagesDaily = this.totalPagesDailySignal.asReadonly();
  public consumptionUnitsDaily = signal<string>('');
  public hasEstimatedConsumptionsDaily = signal(false);
  public dailyHourlyConsumptionsError = signal<string | null>(null);
  public consumptionUnitsByLocation = signal<string>('');
  public consumptionsTableByMeter = signal<IMeterDataDistributed[]>([]);

  public readingsPage = signal<IReadingPage | null>(null);
  public errorR = signal<string | null>(null);

  private metersForSupplyTypeId = signal<number | null>(null);
  private dailyChartFingerprint = signal<DailyChartFingerprint | null>(null);
  private dailyTableFingerprint = signal<DailyChartFingerprint | null>(null);
  private monthlyChartFingerprint = signal<MonthlyChartFingerprint | null>(null);
  private monthlyTableFingerprint = signal<MonthlyChartFingerprint | null>(null);
  private monthlyDistributedTableFingerprint = signal<MonthlyDistributedTableFingerprint | null>(
    null,
  );
  private monthlyTablePageSignal = signal(0);
  private monthlyDistributedTablePageSignal = signal(0);
  private dailyTablePageSignal = signal(0);
  private tenantIdSignal = signal<string | null>(null);

  // --- Computed ---
  public hasMeters = computed(() => this.tenantMeters().length > 0);

  public lastReading = computed(() => {
    const reading = this.consumptions();

    if (reading.length === 0) {
      return null;
    }

    return reading.reduce((a, b) => {
      return new Date(b.consumptionDate) > new Date(a.consumptionDate) ? b : a;
    });
  });

  hasMetersForSupply(supplyTypeId: number): boolean {
    return this.meters().length > 0 && this.metersForSupplyTypeId() === supplyTypeId;
  }

  isForTenant(tenantId: string): boolean {
    return this.tenantIdSignal() === tenantId;
  }

  getDefaultMonthlyRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setFullYear(to.getFullYear() - 2);
    return { from: this.formatDateFromDate(from), to: this.formatDateFromDate(to) };
  }

  getDefaultDailyRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return { from: this.formatDateFromDate(from), to: this.formatDateFromDate(to) };
  }

  private formatDateFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Precarga «Mis consumos» (gráfica + tabla mensual, primer suministro/contador, 24 meses).
   */
  async prefetchMonthlyReadingsPage(tenantId: string): Promise<void> {
    const supplies = await this.getSupplies(tenantId);
    if (!supplies.length) return;

    const firstSupply = supplies[0];
    const supplyTypeId = firstSupply.supplyTypeId ?? firstSupply.id;
    if (!supplyTypeId) return;

    const meters = await this.getMeters(tenantId, supplyTypeId);
    if (!meters.length) return;

    const firstMeter = meters[0];
    const { from, to } = this.getDefaultMonthlyRange();
    const fingerprint: MonthlyChartFingerprint = {
      tenantId,
      supplyTypeId,
      meterId: firstMeter.meterId,
      frequency: 'MONTH',
      from,
      to,
    };

    await this.getConsumptions(
      { tenantId, supplyTypeId, meterId: firstMeter.meterId, frequency: 'MONTH', from, to },
      { fingerprint },
    );

    await this.getConsumptionsTable(
      {
        tenantId,
        supplyTypeId,
        meterId: firstMeter.meterId,
        frequency: 'MONTH',
        from,
        to,
        page: 0,
        size: 10,
        sortField: 'consumptionDate',
        sort: 'desc',
      },
      { fingerprint },
    );
  }

  /**
   * Precarga «Lecturas diarias» (gráfica, primer suministro/contador, 30 días / DAY).
   */
  async prefetchDailyReadingsPage(tenantId: string): Promise<void> {
    let supplies = this.supplies();
    if (!this.isForTenant(tenantId) || supplies.length === 0) {
      supplies = await this.getSupplies(tenantId);
    }
    if (!supplies.length) return;

    const firstSupply = supplies[0];
    const supplyTypeId = firstSupply.supplyTypeId ?? firstSupply.id;
    if (!supplyTypeId) return;

    let meters = this.meters();
    if (!this.hasMetersForSupply(supplyTypeId)) {
      meters = await this.getMeters(tenantId, supplyTypeId);
    }
    if (!meters.length) return;

    const firstMeter = meters[0];
    const { from, to } = this.getDefaultDailyRange();
    const fingerprint: DailyChartFingerprint = {
      tenantId,
      supplyTypeId,
      meterId: firstMeter.meterId,
      frequency: 'DAY',
      from,
      to,
      formatType: '0',
    };

    await this.fetchConsumptionsDailyHourly(
      tenantId,
      supplyTypeId,
      firstMeter.meterId,
      'DAY',
      from,
      to,
      { manageSpinner: false, fingerprint },
    );
  }

  matchesDailyChartFingerprint(fingerprint: DailyChartFingerprint): boolean {
    const cached = this.dailyChartFingerprint();
    if (!cached) {
      return false;
    }

    return (
      cached.tenantId === fingerprint.tenantId &&
      cached.supplyTypeId === fingerprint.supplyTypeId &&
      cached.meterId === fingerprint.meterId &&
      cached.frequency === fingerprint.frequency &&
      cached.from === fingerprint.from &&
      cached.to === fingerprint.to &&
      cached.formatType === fingerprint.formatType
    );
  }

  private recordDailyChartFingerprint(fingerprint: DailyChartFingerprint): void {
    this.dailyChartFingerprint.set(fingerprint);
  }

  isDailyTableForContext(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    frequency: string,
    from: string,
    to: string,
    formatType: string,
  ): boolean {
    const cached = this.dailyTableFingerprint();
    if (!cached) {
      return false;
    }

    return (
      cached.tenantId === tenantId &&
      cached.supplyTypeId === supplyTypeId &&
      cached.meterId === meterId &&
      cached.frequency === frequency &&
      cached.from === from &&
      cached.to === to &&
      cached.formatType === formatType
    );
  }

  /** Cache reutilizable al entrar: mismo contexto y página 0. */
  isReusableDailyTableCache(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    frequency: string,
    from: string,
    to: string,
    formatType: string,
  ): boolean {
    return (
      this.isDailyTableForContext(
        tenantId,
        supplyTypeId,
        meterId,
        frequency,
        from,
        to,
        formatType,
      ) && this.dailyTablePageSignal() === 0
    );
  }

  private recordDailyTableFingerprint(fingerprint: DailyChartFingerprint): void {
    if (!fingerprint.from) {
      return;
    }
    this.dailyTableFingerprint.set(fingerprint);
  }

  matchesMonthlyChartFingerprint(fingerprint: MonthlyChartFingerprint): boolean {
    const cached = this.monthlyChartFingerprint();
    if (!cached) {
      return false;
    }

    return (
      cached.tenantId === fingerprint.tenantId &&
      cached.supplyTypeId === fingerprint.supplyTypeId &&
      cached.meterId === fingerprint.meterId &&
      cached.frequency === fingerprint.frequency &&
      cached.from === fingerprint.from &&
      cached.to === fingerprint.to &&
      this.consumptions().length > 0
    );
  }

  private recordMonthlyChartFingerprint(fingerprint: MonthlyChartFingerprint): void {
    this.monthlyChartFingerprint.set(fingerprint);
  }

  isMonthlyTableForContext(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    from: string,
    to: string,
  ): boolean {
    const cached = this.monthlyTableFingerprint();
    if (!cached) {
      return false;
    }

    return (
      cached.tenantId === tenantId &&
      cached.supplyTypeId === supplyTypeId &&
      cached.meterId === meterId &&
      cached.from === from &&
      cached.to === to
    );
  }

  /** Cache reutilizable al entrar: mismo contexto y página 0. */
  isReusableMonthlyTableCache(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    from: string,
    to: string,
  ): boolean {
    return (
      this.isMonthlyTableForContext(tenantId, supplyTypeId, meterId, from, to) &&
      this.monthlyTablePageSignal() === 0
    );
  }

  isMonthlyDistributedTableForContext(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
  ): boolean {
    const cached = this.monthlyDistributedTableFingerprint();
    if (!cached) {
      return false;
    }

    return (
      cached.tenantId === tenantId &&
      cached.supplyTypeId === supplyTypeId &&
      cached.from === from &&
      cached.to === to
    );
  }

  /** Cache reutilizable al entrar: mismo contexto y página 0. */
  isReusableMonthlyDistributedTableCache(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
  ): boolean {
    return (
      this.isMonthlyDistributedTableForContext(tenantId, supplyTypeId, from, to) &&
      this.monthlyDistributedTablePageSignal() === 0
    );
  }

  private recordMonthlyTableFingerprint(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    frequency: string,
    from: string,
    to: string,
  ): void {
    if (!from || !to) {
      return;
    }

    this.monthlyTableFingerprint.set({
      tenantId,
      supplyTypeId,
      meterId,
      frequency: frequency as MonthlyChartFingerprint['frequency'],
      from,
      to,
    });
  }

  private recordMonthlyDistributedTableFingerprint(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
  ): void {
    if (!from || !to) {
      return;
    }

    this.monthlyDistributedTableFingerprint.set({
      tenantId,
      supplyTypeId,
      from,
      to,
    });
  }

  /**
   * Obtener Suministros
   */
  async getSupplies(tenantId: string): Promise<ISupply[]> {
    this.error.set(null);

    try {
      const res = await firstValueFrom(
        this.http.get<{ data: ISupply[] }>(`${environment.apiUrl}/tenant/${tenantId}/supplies`),
      );
      const data = res.data ?? [];
      this.supplies.set(data);
      this.tenantIdSignal.set(tenantId);
      return data;
    } catch (err: any) {
      this.error.set(`Error fetching supplies: ${err.status}`);
      return [];
    }
  }

  /**
   * Obtener Contadores por tipo de suministro
   */
  async getMeters(tenantId: string, supplyTypeId: number): Promise<IMeter[]> {
    /*     this.$spinner.show(); */
    try {
      const res = await firstValueFrom(
        this.http.get<{ data: IMeter[] }>(
          `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/meters`,
        ),
      );
      const data = res.data ?? [];
      this.meters.set(data);
      this.metersForSupplyTypeId.set(supplyTypeId);
      return data;
    } catch (err) {
      this.error.set('Error fetching meters');
      this.meters.set([]);
      this.metersForSupplyTypeId.set(null);
      return [];
    }
  }

  async createSettlementSimulation(
    tenantId: string,
    meterId: number,
    reading: number,
    readingDate: string,
  ): Promise<ISettlementSimulation> {
    let params = new HttpParams().set('reading', String(reading));
    if (readingDate) {
      params = params.set('readingDate', readingDate);
    }

    const res = await firstValueFrom(
      this.http.post<IResponse<ISettlementSimulation>>(
        `${environment.apiUrl}/tenant/${tenantId}/meters/${meterId}/simulation`,
        null,
        { params },
      ),
    );

    if (!res.data) {
      throw new Error('Empty simulation response');
    }
    return res.data;
  }

  downloadSettlementSimulation(tenantId: string, simulationId: number) {
    return this.http.get(
      `${environment.apiUrl}/tenant/${tenantId}/simulations/${simulationId}/stream`,
      { responseType: 'blob' },
    );
  }

  async getConsumptions(
    params: {
      tenantId: string;
      supplyTypeId: number;
      meterId: number;
      frequency: string;
      from: string;
      to: string;
    },
    options: MonthlyMeteringFetchOptions = {},
  ): Promise<IConsumptionDetail[]> {
    const { tenantId, supplyTypeId, meterId, frequency, from, to } = params;

    // 1. Estado inicial
    this.isLoading.set(true);
    this.error.set(null);

    // 2. Construcción de URL y Query Params
    const url = `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions`;

    const httpParams = new HttpParams()
      .set('frequency', frequency)
      .set('meterId', meterId.toString())
      .set('from', from)
      .set('to', to);

    try {
      // 3. Petición (usamos lastValueFrom para manejarlo como Promise async/await)
      const response = await lastValueFrom(this.http.get<any>(url, { params: httpParams }));

      // 4. Procesamiento de datos (mapeo de la estructura data -> consumptions)
      const dataMap = response?.data || {};
      const rawList = dataMap.consumptions || [];

      const mappedConsumptions: IConsumptionDetail[] = rawList; // Si el JSON coincide con la interfaz

      // 5. Actualización de Signals
      this.consumptions.set(mappedConsumptions);
      this.consumptionUnits.set(dataMap.consumptionUnits || '');

      // La gráfica pide el listado completo (sin page/size). Si el total paginado
      // aún no está o es menor, usamos esta longitud como total real.
      if (mappedConsumptions.length > this.totalElementsSignal()) {
        this.totalElementsSignal.set(mappedConsumptions.length);
        this.totalPagesSignal.set(Math.max(1, Math.ceil(mappedConsumptions.length / 10)));
      }

      if (options.fingerprint) {
        this.recordMonthlyChartFingerprint(options.fingerprint);
      }

      return mappedConsumptions;
    } catch (e: any) {
      // 6. Manejo de errores (no tocar tabla/paginación: suele ir en paralelo con getConsumptionsTable)
      const errorMsg = e.status ? `Error: ${e.status}` : `Connection error: ${e.message}`;
      this.error.set(errorMsg);
      console.error(errorMsg);
      this.consumptions.set([]);
      this.consumptionUnits.set('');
      return [];
    } finally {
      // 7. Finalizar carga
      this.isLoading.set(false);
    }
  }

  async getConsumptionsTable(
    params: {
      tenantId: string;
      supplyTypeId: number;
      meterId: number;
      frequency: string;
      from: string;
      to: string;
      page?: number;
      size?: number;
      sortField?: string;
      sort?: string;
    },
    options: MonthlyMeteringFetchOptions = {},
  ): Promise<IConsumptionDetail[]> {
    const {
      tenantId,
      supplyTypeId,
      meterId,
      frequency,
      from,
      to,
      sortField = 'consumptionDate',
      sort = 'asc',
      page = 0,
      size = 10,
    } = params;

    this.isLoading.set(true);
    this.error.set(null);

    const url = `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions`;

    const httpParams = new HttpParams()
      .set('frequency', frequency)
      .set('meterId', meterId.toString())
      .set('from', from)
      .set('to', to)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', `${sortField},${sort}`);
    try {
      const response = await lastValueFrom(this.http.get<any>(url, { params: httpParams }));
      const dataMap = response?.data || {};
      const rawList = Array.isArray(dataMap.consumptions)
        ? dataMap.consumptions
        : Array.isArray(dataMap.content)
          ? dataMap.content
          : [];
      const mappedConsumptions: IConsumptionDetail[] = rawList;

      this.consumptionsTable.set(mappedConsumptions);
      this.consumptionUnits.set(dataMap.consumptionUnits || '');

      const { totalElements, totalPages } = this.resolveConsumptionPageMeta(
        response,
        dataMap,
        mappedConsumptions.length,
        size,
        this.consumptions().length,
        this.totalElementsSignal(),
      );
      this.totalElementsSignal.set(totalElements);
      this.totalPagesSignal.set(totalPages);
      this.monthlyTablePageSignal.set(page);

      this.recordMonthlyTableFingerprint(tenantId, supplyTypeId, meterId, frequency, from, to);

      if (options.fingerprint) {
        this.recordMonthlyChartFingerprint(options.fingerprint);
      }

      return mappedConsumptions;
    } catch (e: any) {
      const errorMsg = e.status ? `Error: ${e.status}` : `Connection error: ${e.message}`;
      this.error.set(errorMsg);
      this.consumptionsTable.set([]);
      this.consumptionUnits.set('');
      this.totalElementsSignal.set(0);
      this.totalPagesSignal.set(0);
      this.monthlyTablePageSignal.set(0);
      this.isLoading.set(false);
      console.error(errorMsg);
      return [];
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Resuelve el total real de la paginación (no el tamaño de la página cargada).
   * Compatibilidad con varias formas de respuesta del API.
   */
  private resolveConsumptionPageMeta(
    response: unknown,
    dataMap: Record<string, unknown>,
    pageItemCount: number,
    pageSize: number,
    fullListCount = 0,
    knownTotal = 0,
  ): { totalElements: number; totalPages: number } {
    const responseObj =
      response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
    const pageObj =
      dataMap['page'] && typeof dataMap['page'] === 'object'
        ? (dataMap['page'] as Record<string, unknown>)
        : {};
    const paginationObj =
      dataMap['pagination'] && typeof dataMap['pagination'] === 'object'
        ? (dataMap['pagination'] as Record<string, unknown>)
        : {};

    const candidates = [
      dataMap['totalElements'],
      dataMap['totalCount'],
      pageObj['totalElements'],
      paginationObj['totalElements'],
      responseObj['totalElements'],
      fullListCount > pageItemCount ? fullListCount : null,
      knownTotal > pageItemCount ? knownTotal : null,
      pageItemCount,
    ];

    let totalElements = pageItemCount;
    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value >= pageItemCount) {
        totalElements = value;
        break;
      }
    }

    const totalPagesCandidates = [
      dataMap['totalPages'],
      pageObj['totalPages'],
      paginationObj['totalPages'],
      responseObj['totalPages'],
    ];
    let totalPages = Math.max(1, Math.ceil(totalElements / Math.max(pageSize, 1)));
    for (const candidate of totalPagesCandidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value >= 1) {
        totalPages = value;
        break;
      }
    }

    return { totalElements, totalPages };
  }

  private resolveDailyChartPageSize(frequency: string, from: string, to: string): number {
    if (frequency === 'HOUR') {
      return 24;
    }
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);
    let buckets = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
    if (frequency === 'WEEK') {
      buckets = Math.ceil(buckets / 7);
    } else if (frequency === 'MONTH') {
      buckets =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    } else if (frequency === 'YEAR') {
      buckets = end.getFullYear() - start.getFullYear() + 1;
    }
    return Math.min(200, Math.max(1, buckets));
  }

  clearDailyHourlyConsumptionsError(): void {
    this.dailyHourlyConsumptionsError.set(null);
  }

  private formatDailyHourlyFetchError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return 'No se pudieron cargar los consumos. Compruebe su conexión e inténtelo de nuevo.';
      }
      if (err.status === 403) {
        return 'No tiene permiso para consultar estos consumos.';
      }
      if (err.status === 404) {
        return 'No se encontraron consumos para la selección actual.';
      }
      return `No se pudieron cargar los consumos (error ${err.status}). Inténtelo de nuevo más tarde.`;
    }
    return 'No se pudieron cargar los consumos. Inténtelo de nuevo más tarde.';
  }

  async fetchConsumptionsDailyHourly(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    frequency: string,
    from: string,
    to: string,
    options: DailyMeteringFetchOptions = {},
  ): Promise<IConsumptionDetail[]> {
    const manageSpinner = options.manageSpinner ?? true;

    if (manageSpinner) {
      this.$spinner.show();
    }

    this.consumptionsDaily.set([]);

    let url = `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions/`;
    let params = new HttpParams().set('meterId', meterId.toString());

    if (frequency !== 'HOUR') {
      url += 'daily';
      params = params
        .set('frequency', frequency)
        .set('from', from)
        .set('to', to)
        .set('estimateGaps', 'true')
        .set('page', '0')
        .set('size', this.resolveDailyChartPageSize(frequency, from, to).toString())
        .set('sort', 'consumptionDate,asc');
    } else {
      url += 'hourly';
      params = params
        .set('from', from)
        .set('estimateGaps', 'true')
        .set('page', '0')
        .set('size', '24')
        .set('sort', 'consumptionDate,asc');
    }

    try {
      const res = await firstValueFrom(this.http.get<any>(url, { params }));

      const rawList = res.data?.consumptions ?? [];
      this.consumptionsDaily.set(rawList);
      this.consumptionUnitsDaily.set(res.data?.consumptionUnits ?? '');
      this.hasEstimatedConsumptionsDaily.set(Boolean(res.data?.hasEstimatedConsumptions));

      if (rawList.length > this.totalElementsDailySignal()) {
        this.totalElementsDailySignal.set(rawList.length);
        this.totalPagesDailySignal.set(Math.max(1, Math.ceil(rawList.length / 10)));
      }

      if (options.fingerprint) {
        this.recordDailyChartFingerprint(options.fingerprint);
      }

      return rawList;
    } catch (err) {
      this.consumptionsDaily.set([]);
      this.consumptionUnitsDaily.set('');
      this.hasEstimatedConsumptionsDaily.set(false);
      this.dailyHourlyConsumptionsError.set(this.formatDailyHourlyFetchError(err));
      return [];
    } finally {
      if (manageSpinner) {
        this.$spinner.hide();
      }
    }
  }

  async fetchConsumptionsDailyHourlyTable(
    tenantId: string,
    supplyTypeId: number,
    meterId: number,
    frequency: string,
    from: string,
    to: string,
    page?: number,
    size?: number,
    sortField?: string,
    sort?: string,
    options: DailyMeteringFetchOptions = {},
  ): Promise<IConsumptionDetail[]> {
    const manageSpinner = options.manageSpinner ?? true;

    if (manageSpinner) {
      this.$spinner.show();
    }

    let url = `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions/`;
    let params = new HttpParams().set('meterId', meterId.toString());

    if (frequency !== 'HOUR') {
      url += 'daily';
      params = params.set('frequency', frequency).set('from', from).set('to', to);
    } else {
      url += 'hourly';
      params = params.set('from', from);
    }

    let pageParam = page != null ? page : 0;
    let sizeParam = size != null ? size : 10;
    let sortFieldParam = sortField != null ? sortField : 'consumptionDate';
    let sortParam = sort != null ? sort : 'asc';

    params = params
      .set('page', pageParam.toString())
      .set('size', sizeParam.toString())
      .set('sort', `${sortFieldParam},${sortParam}`)
      .set('estimateGaps', 'true');

    try {
      const res = await firstValueFrom(this.http.get<any>(url, { params }));
      const dataMap = (res?.data ?? {}) as Record<string, unknown>;
      const rawList = Array.isArray(dataMap['consumptions'])
        ? (dataMap['consumptions'] as IConsumptionDetail[])
        : Array.isArray(dataMap['content'])
          ? (dataMap['content'] as IConsumptionDetail[])
          : [];

      this.consumptionsTableDaily.set(rawList);
      this.consumptionUnitsDaily.set(String(dataMap['consumptionUnits'] ?? ''));
      this.hasEstimatedConsumptionsDaily.set(Boolean(dataMap['hasEstimatedConsumptions']));

      const { totalElements, totalPages } = this.resolveConsumptionPageMeta(
        res,
        dataMap,
        rawList.length,
        sizeParam,
        this.consumptionsDaily().length,
        this.totalElementsDailySignal(),
      );
      this.totalElementsDailySignal.set(totalElements);
      this.totalPagesDailySignal.set(totalPages);
      this.dailyTablePageSignal.set(pageParam);

      if (options.tableFingerprint) {
        this.recordDailyTableFingerprint(options.tableFingerprint);
      }

      return rawList;
    } catch (err) {
      this.consumptionsTableDaily.set([]);
      this.dailyTablePageSignal.set(0);
      this.dailyHourlyConsumptionsError.set(this.formatDailyHourlyFetchError(err));
      return [];
    } finally {
      if (manageSpinner) {
        this.$spinner.hide();
      }
    }
  }
  /**
   * Consumos agrupados por ubicación
   */
  async getConsumptionsByLocation(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
    page?: number,
    size?: number,
    sortField?: string,
    sort?: string,
    manageSpinner = true,
  ) {
    if (manageSpinner) {
      this.$spinner.show();
    }

    let params = new HttpParams().set('from', from).set('to', to);
    let pageParam = page != null ? page : 0;
    let sizeParam = size != null ? size : 10;
    let sortFieldParam = sortField != null ? sortField : 'meterId';
    let sortParam = sort != null ? sort : 'asc';

    params = params
      .set('page', pageParam.toString())
      .set('size', sizeParam.toString())
      .set('sort', `${sortFieldParam},${sortParam}`);

    try {
      const res = await firstValueFrom(
        this.http.get<IMeterConsumptionDistributedResponse>(
          `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions/by-meter`,
          { params },
        ),
      );
      this.consumptionsDistributed.set(res.data);
      this.consumptionUnitsByLocation.set(res.data[0].consumptionUnits ?? '');
    } finally {
      if (manageSpinner) {
        this.$spinner.hide();
      }
    }
  }
  async getConsumptionsByLocationByMeterTable(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
    page?: number,
    size?: number,
    sortField?: string,
    sort?: string,
  ) {
    this.$spinner.show();

    let params = new HttpParams().set('from', from).set('to', to);
    let pageParam = page != null ? page : 0;
    let sizeParam = size != null ? size : 10;
    let sortFieldParam = sortField != null ? sortField : 'meterId';
    let sortParam = sort != null ? sort : 'asc';

    params = params
      .set('page', pageParam.toString())
      .set('size', sizeParam.toString())
      .set('sort', `${sortFieldParam},${sortParam}`);

    try {
      const res = await firstValueFrom(
        this.http.get<IMeterConsumptionDistributedResponse>(
          `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions/by-meter`,
          { params },
        ),
      );
      this.consumptionsTableByMeter.set(res.data);
      this.consumptionUnitsByLocation.set(res.data[0].consumptionUnits ?? '');
      this.monthlyDistributedTablePageSignal.set(pageParam);
      this.recordMonthlyDistributedTableFingerprint(tenantId, supplyTypeId, from, to);
    } finally {
      this.$spinner.hide();
    }
  }

  async fetchConsumptionsByMeterForExport(
    tenantId: string,
    supplyTypeId: number,
    from: string,
    to: string,
  ): Promise<IMeterDataDistributed[]> {
    const params = new HttpParams()
      .set('from', from)
      .set('to', to)
      .set('page', '0')
      .set('size', '10000')
      .set('sort', 'meterId,asc');

    const res = await firstValueFrom(
      this.http.get<IMeterConsumptionDistributedResponse>(
        `${environment.apiUrl}/tenant/${tenantId}/supplies/${supplyTypeId}/consumptions/by-meter`,
        { params },
      ),
    );

    return Array.isArray(res.data) ? res.data : [];
  }

  /**
   * Enviar Lectura Manual (Multipart/FormData)
   */
  async sendManualReading(
    tenantId: string,
    meterId: string,
    value: string,
    file?: File,
  ): Promise<boolean> {
    const formData = new FormData();
    formData.append('meterId', meterId);
    formData.append('value', value);

    if (file) {
      formData.append('attachments', file, file.name);
    }

    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/tenant/${tenantId}/send-manual-reading`, formData),
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Limpiar estado
   */
  public clear(): void {
    this.tenantIdSignal.set(null);
    this.supplies.set([]);
    this.meters.set([]);
    this.metersForSupplyTypeId.set(null);
    this.consumptions.set([]);
    this.consumptionsDistributed.set([]);
    this.consumptionsTable.set([]);
    this.consumptionsTableByMeter.set([]);
    this.consumptionUnits.set('');
    this.consumptionUnitsByLocation.set('');
    this.totalElementsSignal.set(0);
    this.totalPagesSignal.set(0);
    this.consumptionsDaily.set([]);
    this.consumptionsTableDaily.set([]);
    this.hasEstimatedConsumptionsDaily.set(false);
    this.dailyHourlyConsumptionsError.set(null);
    this.consumptionUnitsDaily.set('');
    this.totalElementsDailySignal.set(0);
    this.totalPagesDailySignal.set(0);
    this.dailyChartFingerprint.set(null);
    this.dailyTableFingerprint.set(null);
    this.monthlyChartFingerprint.set(null);
    this.monthlyTableFingerprint.set(null);
    this.monthlyDistributedTableFingerprint.set(null);
    this.monthlyTablePageSignal.set(0);
    this.monthlyDistributedTablePageSignal.set(0);
    this.dailyTablePageSignal.set(0);
    this.tenantMeters.set([]);
    this.hasFetchedTenantMeters = false;
    this.error.set(null);
    this.errorR.set(null);
    this.isLoading.set(false);
  }
}
